import { useEffect } from "react";

interface UseInboxKeyboardArgs {
  notifications: { id: string }[];
  focusedIndex: number | null;
  setFocusedIndex: (index: number | null) => void;
  onSelect: (id: string) => void;
  onArchive: (id: string) => void;
  onMarkRead: (id: string) => void;
  onMarkUnread: (id: string) => void;
  isReadMap: Record<string, boolean>;
}

export function useInboxKeyboard({
  notifications,
  focusedIndex,
  setFocusedIndex,
  onSelect,
  onArchive,
  onMarkRead,
  onMarkUnread,
  isReadMap,
}: UseInboxKeyboardArgs) {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Don't capture when user is typing in an input/textarea
      const target = e.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        return;
      }

      const count = notifications.length;
      if (count === 0) return;

      const currentIndex = focusedIndex ?? -1;

      switch (e.key) {
        case "j":
        case "ArrowDown": {
          e.preventDefault();
          const next = Math.min(currentIndex + 1, count - 1);
          setFocusedIndex(next);
          break;
        }
        case "k":
        case "ArrowUp": {
          e.preventDefault();
          const prev = Math.max(currentIndex - 1, 0);
          setFocusedIndex(prev);
          break;
        }
        case "Enter": {
          if (focusedIndex !== null) {
            e.preventDefault();
            onSelect(notifications[focusedIndex].id);
          }
          break;
        }
        case "Escape": {
          e.preventDefault();
          setFocusedIndex(null);
          onSelect(""); // clear selection -- parent handles null
          break;
        }
        case "e":
        case "E": {
          if (focusedIndex !== null) {
            e.preventDefault();
            onArchive(notifications[focusedIndex].id);
            // Advance focus to next item (or stay at same index)
            const next = Math.min(focusedIndex, count - 2);
            setFocusedIndex(next >= 0 ? next : null);
          }
          break;
        }
        case "u":
        case "U": {
          if (focusedIndex !== null) {
            e.preventDefault();
            const id = notifications[focusedIndex].id;
            if (isReadMap[id]) {
              onMarkUnread(id);
            } else {
              onMarkRead(id);
            }
          }
          break;
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    notifications,
    focusedIndex,
    setFocusedIndex,
    onSelect,
    onArchive,
    onMarkRead,
    onMarkUnread,
    isReadMap,
  ]);
}
